import { useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { CaptureRail } from '../browser/capture-rail';
import { hostOf } from '../browser/target-url';
import { useCaptures } from '../browser/use-captures';
import { ArrowLeftIcon, CameraIcon, MonitorIcon } from '../lib/icons';
import { useUiStore } from '../store/ui-store';
import { useExtensionSession } from './use-extension-session';

/**
 * `/browse` for a product only the user's machine can reach.
 *
 * Same screen as the worker's — bar across the top, captures rail on the right
 * — but the left half cannot show the page, because Cloud Run has no route to
 * `localhost`. So it shows the only thing worth showing instead: where the
 * shutter is, and what has arrived.
 *
 * Designed as a *stage*, not a form. The instructions sit on the same warm
 * canvas the landing screen uses, with one lime glow behind them, because this
 * is the empty half of a workspace rather than a settings page — and the three
 * steps are a timeline, since they happen in order and the middle one is where
 * the user actually leaves.
 */

/** Placeholders: swap for the real store listing and screen recordings. */
const INSTALL_URL = 'https://example.com/floto-extension';
const INSTALL_GIF = null;
const CAPTURE_GIF = null;

export function ExtensionBrowse({
  projectId,
  sessionIdFromUrl,
  target,
}: {
  /** The project a new session goes in (`?project=`), if one was picked. */
  projectId: null | string;
  sessionIdFromUrl: null | string;
  target: string;
}) {
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();

  const onResolved = useCallback(
    (sessionId: string) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('session', sessionId);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const sessionId = useExtensionSession(
    target,
    sessionIdFromUrl,
    projectId,
    onResolved,
  );
  const { captures, removeCard } = useCaptures(sessionId);

  const extensionSeen = useUiStore((state) => state.extensionSeen);
  const incoming = useUiStore((state) => state.incoming);
  const failed = useUiStore((state) => state.failed);

  const started = captures.length > 0;

  return (
    <div className="flex h-svh flex-col overflow-hidden bg-warm">
      <TopBar target={target} />

      <div className="flex min-h-0 flex-1">
        <Stage
          extensionSeen={extensionSeen}
          started={started}
          target={target}
        />

        <CaptureRail
          actionSlot={<RailStatus failed={failed} incoming={incoming} />}
          captures={captures}
          emptyHint="They appear here the moment they arrive."
          emptyTitle="Nothing captured yet"
          footerNote="Captured at your screen's own resolution"
          onOpenCanvas={() => {
            if (sessionId) void navigate(`/sessions/${sessionId}`);
          }}
          onRemove={removeCard}
        />
      </div>
    </div>
  );
}

/**
 * Deliberately not `BrowserToolbar`: there is no history to walk, no address to
 * type into and no viewport to resize, and a row of dead controls would be a
 * worse lie than no controls at all.
 */
function TopBar({ target }: { target: string }) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-hairline bg-surface px-4">
      <Link
        className="flex h-9 items-center gap-1.5 rounded-button px-2.5 text-ui font-medium text-text-secondary transition hover:bg-warm hover:text-text-primary"
        to="/"
      >
        <ArrowLeftIcon className="size-4" />
        Products
      </Link>

      <div className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-pill bg-warm pr-2 pl-3.5 ring-1 ring-hairline">
        <MonitorIcon className="size-4 shrink-0 text-text-muted" />
        <span className="min-w-0 flex-1 truncate text-ui text-text-secondary">
          {hostOf(target)}
        </span>
        <span className="shrink-0 rounded-pill bg-surface px-2.5 py-0.5 text-tag font-semibold text-text-secondary ring-1 ring-hairline">
          your machine
        </span>
      </div>

      <a
        className="flex h-9 shrink-0 items-center gap-1.5 rounded-button px-3 text-ui font-medium text-text-primary ring-1 ring-hairline-alt transition hover:bg-warm active:scale-[0.98]"
        href={target}
        rel="noreferrer"
        target="_blank"
      >
        Open the site
      </a>
    </header>
  );
}

function Stage({
  extensionSeen,
  started,
  target,
}: {
  extensionSeen: boolean;
  started: boolean;
  target: string;
}) {
  return (
    <div className="relative min-w-0 flex-1 overflow-y-auto">
      {/* One lime glow, the same signature the landing screen uses — this is
          the empty half of a workspace, not a dialog. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -left-16 size-[520px] rounded-pill bg-highlight/25 blur-[120px]"
      />

      <div className="relative mx-auto w-full max-w-[600px] px-10 py-12">
        <StatusChip seen={extensionSeen} />

        <h1 className="!mt-5 !mb-0 !text-[clamp(1.75rem,2.6vw,2.25rem)] !leading-[1.05] !font-bold !tracking-[-0.03em] !text-text-primary">
          {extensionSeen ? (
            started ? (
              <>
                Keep going — <Marked>every shot lands on the right.</Marked>
              </>
            ) : (
              <>
                Capture <Marked>{hostOf(target)}</Marked> from your own browser.
              </>
            )
          ) : (
            <>
              This one runs on <Marked>your machine.</Marked>
            </>
          )}
        </h1>

        <p className="mt-3 max-w-[46ch] text-body text-text-secondary">
          {extensionSeen
            ? 'Floto has no browser here — you are the browser. Shoot each screen you want reviewed and they arrive in the rail beside this.'
            : 'Our hosted browser cannot reach localhost, so the Chrome extension takes the screenshots and sends them here. One install, once.'}
        </p>

        {extensionSeen ? (
          <CaptureSteps gif={CAPTURE_GIF} started={started} url={target} />
        ) : (
          <InstallSteps gif={INSTALL_GIF} />
        )}
      </div>
    </div>
  );
}

/** The highlighter stroke from the landing screen — same trick, same reason. */
function Marked({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative isolate inline-block after:absolute after:inset-x-[-0.12em] after:bottom-[0.08em] after:-z-10 after:h-[0.42em] after:-rotate-[0.6deg] after:rounded-xs after:bg-highlight after:content-['']">
      {children}
    </span>
  );
}

function StatusChip({ seen }: { seen: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-2 rounded-pill py-1.5 pr-3.5 pl-2.5 text-xs font-semibold duration-500 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none',
        seen
          ? 'bg-ink text-white'
          : 'bg-surface text-text-secondary ring-1 ring-hairline-alt',
      ].join(' ')}
    >
      <span className="relative flex size-2">
        {seen ? (
          <span className="absolute inset-0 animate-ping rounded-pill bg-highlight opacity-60 motion-reduce:animate-none" />
        ) : null}
        <span
          className={[
            'relative size-2 rounded-pill',
            seen ? 'bg-highlight' : 'bg-warning',
          ].join(' ')}
        />
      </span>
      {seen ? 'Extension connected' : 'Extension not detected'}
    </span>
  );
}

/**
 * The steps, as a timeline: they happen in order, and the one the user leaves
 * the page for is worth marking as the live one.
 */
function Steps({ children }: { children: React.ReactNode }) {
  return (
    <ol className="relative mt-8 space-y-5 before:absolute before:top-3 before:bottom-3 before:left-[13px] before:w-px before:bg-hairline-alt before:content-['']">
      {children}
    </ol>
  );
}

function Step({
  active = false,
  children,
  index,
  title,
}: {
  active?: boolean;
  children?: React.ReactNode;
  index: number;
  title: React.ReactNode;
}) {
  return (
    <li
      className="relative flex gap-4 duration-500 animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none"
      style={{ animationDelay: `${String(index * 70)}ms` }}
    >
      <span
        className={[
          'relative z-10 mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-pill text-xs font-bold ring-4 ring-warm transition',
          active
            ? 'bg-highlight text-ink'
            : 'bg-surface text-text-secondary ring-4 ring-warm outline outline-hairline-alt',
        ].join(' ')}
      >
        {index}
      </span>
      <div className="min-w-0 pt-0.5">
        <p className="text-ui-lg leading-snug font-semibold text-text-primary">
          {title}
        </p>
        {children ? (
          <p className="mt-1 text-ui leading-relaxed text-text-secondary">
            {children}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function InstallSteps({ gif }: { gif: null | string }) {
  return (
    <>
      <Steps>
        <Step
          active
          index={1}
          title={
            <a
              className="underline decoration-highlight decoration-[3px] underline-offset-4 transition hover:text-ink"
              href={INSTALL_URL}
              onClick={() => {
                trackEvent('extension_install_clicked', {
                  source: 'localhost',
                });
              }}
              rel="noreferrer"
              target="_blank"
            >
              Install the Floto Chrome extension
            </a>
          }
        >
          It only ever talks to Floto, and only captures the tab you press it
          on.
        </Step>
        <Step index={2} title="Reload this tab">
          A tab opened before the extension was installed has no way to receive
          captures.
        </Step>
        <Step index={3} title="Come back here and start capturing" />
      </Steps>

      <Media gif={gif} label="Installing the extension" />
    </>
  );
}

function CaptureSteps({
  gif,
  started,
  url,
}: {
  gif: null | string;
  started: boolean;
  url: string;
}) {
  return (
    <>
      <Steps>
        <Step
          active={!started}
          index={1}
          title={
            <>
              Open{' '}
              <a
                className="underline decoration-highlight decoration-[3px] underline-offset-4 transition hover:text-ink"
                href={url}
                rel="noreferrer"
                target="_blank"
              >
                {hostOf(url)}
              </a>{' '}
              in another tab
            </>
          }
        />
        <Step
          active={started}
          index={2}
          title={
            <>
              Click the Floto icon, then <em className="not-italic">Capture</em>
            </>
          }
        >
          Or press <Key>⌘</Key>
          <Key>⇧</Key>
          <Key>Y</Key> without opening it. Scroll, capture, repeat — each one
          uploads on its own.
        </Step>
        <Step index={3} title="Come back to this tab">
          Everything is in the rail, and <strong>Open canvas</strong> lays the
          room out for review.
        </Step>
      </Steps>

      <Media gif={gif} label="Capturing a screen" />

      <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-text-muted">
        <CameraIcon className="mt-0.5 size-3.5 shrink-0" />
        Every capture joins this room. Paste a link on home to open another.
      </p>
    </>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mx-px inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-kbd border border-hairline-alt border-b-2 bg-surface px-1.5 align-middle text-tag font-semibold text-text-primary shadow-soft">
      {children}
    </kbd>
  );
}

/**
 * The space a recording will occupy, drawn as a screen rather than a grey box —
 * an empty frame that looks deliberate reads as "coming", not "broken".
 */
function Media({ gif, label }: { gif: null | string; label: string }) {
  if (gif) {
    return (
      <img
        alt={label}
        className="mt-8 w-full rounded-card border border-hairline shadow-soft"
        src={gif}
      />
    );
  }

  return (
    <figure className="mt-8 overflow-hidden rounded-card border border-hairline bg-surface shadow-soft">
      <div className="flex items-center gap-1.5 border-b border-hairline px-3 py-2.5">
        <span className="size-2 rounded-pill bg-hairline-alt" />
        <span className="size-2 rounded-pill bg-hairline-alt" />
        <span className="size-2 rounded-pill bg-hairline-alt" />
      </div>
      <div className="relative flex h-40 items-center justify-center bg-warm-alt">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.55] [background-image:linear-gradient(to_right,#eceae6_1px,transparent_1px),linear-gradient(to_bottom,#eceae6_1px,transparent_1px)] [background-size:22px_22px]"
        />
        <figcaption className="relative rounded-pill bg-surface px-3 py-1.5 text-tag font-medium text-text-muted ring-1 ring-hairline">
          {label}
        </figcaption>
      </div>
    </figure>
  );
}

/**
 * The rail's action slot. There is no button here on purpose — the shutter is
 * in the extension's popup, and a disabled button in this spot would read as
 * something the user had failed to enable.
 */
function RailStatus({
  failed,
  incoming,
}: {
  failed: boolean;
  incoming: number;
}) {
  const busy = incoming > 0;

  return (
    <div
      className={[
        'rounded-card px-3.5 py-3 text-center transition',
        busy
          ? 'bg-ink text-white'
          : 'border border-dashed border-hairline-alt bg-warm',
      ].join(' ')}
    >
      <p className="flex items-center justify-center gap-2 text-xs leading-snug font-semibold">
        {busy ? (
          <>
            <span className="relative flex size-2">
              <span className="absolute inset-0 animate-ping rounded-pill bg-highlight opacity-70 motion-reduce:animate-none" />
              <span className="relative size-2 rounded-pill bg-highlight" />
            </span>
            {incoming === 1
              ? 'Screenshot on its way…'
              : `${String(incoming)} screenshots on their way…`}
          </>
        ) : (
          <>
            <CameraIcon className="size-3.5 text-text-muted" />
            <span className="text-text-primary">
              Capture from the extension
            </span>
          </>
        )}
      </p>
      {busy ? null : (
        <p className="mt-1 text-tag leading-snug text-text-muted">
          {failed
            ? 'The last one could not be saved — try again.'
            : 'Click the Floto icon on your site.'}
        </p>
      )}
    </div>
  );
}
